import React from "react";
import { useSearchParams } from "react-router-dom";
import { Warning as AlertCircle, Schedule as Clock, Email as Mail, OpenInNew as ExternalLink, ExitToApp } from "@mui/icons-material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Typography, Paper, Grid, Card, CardContent, Button, Container, Divider } from "@mui/material";
import "./ViewLinkStatus.css";

const ViewLinkStatus = () => {
	const [searchParams] = useSearchParams();
	const status = searchParams.get("status") || "expired";

	// Status configuration object for easy expansion
	const statusConfig = {
		expired: {
			icon: AlertCircle,
			title: "Viewing Link Expired",
			message: "The viewing link you're trying to use has expired. For security reasons, viewing links are only valid for a limited time.",
			iconClass: "expired",
			messageIcon: Clock,
		},
		// Future statuses can be added here:
		invalid: {
			icon: AlertCircle,
			title: "Invalid Viewing Link",
			message: "The viewing link you're trying to use is invalid or has been revoked.",
			iconClass: "invalid",
			messageIcon: Clock,
		},
		// revoked: {
		//   icon: Cancel,
		//   title: "Access Link Revoked",
		//   message: "This access link has been revoked by the system administrator.",
		//   iconClass: "revoked",
		//   messageIcon: Security,
		// },
	};

	// Get current status config or fallback to expired
	const currentStatus = statusConfig[status] || statusConfig.expired;
	const StatusIcon = currentStatus.icon;
	const MessageIcon = currentStatus.messageIcon;

	return (
		<Box className="access-link-status">
			<Container maxWidth="md">
				<Paper elevation={3} className="status-container">
					<Box textAlign="center" mb={3}>
						<Box className={`status-icon ${currentStatus.iconClass}`} mb={2}>
							<StatusIcon sx={{ fontSize: 48 }} />
						</Box>

						<Typography variant="h4" component="h1" className="status-title" gutterBottom>
							{currentStatus.title}
						</Typography>

						<Box className={`status-message ${currentStatus.iconClass}`} mb={3}>
							<MessageIcon sx={{ fontSize: 18, mr: 1 }} className={`message-icon ${currentStatus.iconClass}`} />
							<Typography variant="body1">{currentStatus.message}</Typography>
						</Box>
					</Box>

					<Divider sx={{ mb: 3 }} />

					<Box mb={3}>
						<Typography variant="h6" component="h2" gutterBottom textAlign="center">
							What should you do next?
						</Typography>

						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Card className="contact-option" elevation={1}>
									<CardContent>
										<Box className="contact-icon" mb={1}>
											<ExitToApp sx={{ fontSize: 20 }} />
										</Box>
										<Typography variant="h6" component="h3" gutterBottom>
											Go to Verified Portal
										</Typography>
										<Typography variant="body2" color="text.secondary" mb={1}>
											Access your Verified portal to view your certificate.
										</Typography>
										<Button
											href={`https://verified.sgappserver.com/s_Login.jsp`}
											className="contact-link"
											variant="outlined"
											size="small"
											startIcon={<ExternalLink sx={{ fontSize: 14 }} />}
										>
											{"Verified Portal"}
										</Button>
									</CardContent>
								</Card>
							</Grid>
							<Grid item xs={12} md={6}>
								<Card className="contact-option" elevation={1}>
									<CardContent>
										<Box className="contact-icon" mb={1}>
											<Mail sx={{ fontSize: 20 }} />
										</Box>
										<Typography variant="h6" component="h3" gutterBottom>
											Contact Support
										</Typography>
										<Typography variant="body2" color="text.secondary" mb={1}>
											Send us a detailed message about your request
										</Typography>
										<Button
											href={`https://www.synergyhelps.com/portal/en/newticket`}
											className="contact-link"
											variant="outlined"
											size="small"
											startIcon={<ExternalLink sx={{ fontSize: 14 }} />}
										>
											{"Verified Support"}
										</Button>
									</CardContent>
								</Card>
							</Grid>
						</Grid>
					</Box>

					<Divider sx={{ mb: 2 }} />

					<Box className="helpful-tips">
						<Typography variant="h6" component="h3" gutterBottom>
							Helpful Tips
						</Typography>
						<Box component="ul" className="tips-list">
							<Typography component="li" variant="body2">
								Make sure to check your email for any new access links
							</Typography>
							<Typography component="li" variant="body2">
								Access links typically expire within 72 hours for security
							</Typography>
							<Typography component="li" variant="body2">
								Have your case number or reference ID ready when contacting support
							</Typography>
							<Typography component="li" variant="body2">
								Include details about what you were trying to access
							</Typography>
						</Box>
					</Box>
				</Paper>
			</Container>
		</Box>
	);
};

export default ViewLinkStatus;
